import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { isIpadMode, getIpadCoverPath } from "@/lib/guest";

/**
 * Global iPad theme.
 *
 * Renders the masked cover photo as a full-viewport `fixed` background on
 * every guest route (menu / booking / reservations / cabin / journey) whenever
 * the device is in iPad-kiosk mode AND the cabin has a cover photo. Also
 * toggles a `body.ipad-cover` class so the app-wide CSS overrides in
 * `index.css` swap dark card backgrounds for translucent glass and dark
 * copy for light copy.
 *
 * ---------------------------------------------------------------------------
 * Loading optimisation (iteration 124)
 * ---------------------------------------------------------------------------
 * The component now reacts to three signals so the background paints on the
 * FIRST iPad login without needing a hard reload:
 *   1. `yacht-ipad-session-changed` — custom event dispatched by
 *      `setIpadSession()` immediately after localStorage is written. This
 *      catches same-tab logins where the built-in `storage` event doesn't
 *      fire.
 *   2. `storage` — cross-tab / cross-window sync. Rare on iPad but harmless.
 *   3. route changes (via `useLocation`) — belt-and-braces catch for any
 *      code path that mutates localStorage without dispatching the custom
 *      event (older Safari, third-party components, etc).
 * We also preload the cover image via a hidden `new Image()` so `<img>` in
 * the DOM shows the pixels in the first paint frame instead of flashing a
 * black scrim first.
 */
export default function IpadThemeMount() {
  const location = useLocation();
  const readState = useCallback(() => {
    const ipad = isIpadMode();
    const coverPath = getIpadCoverPath();
    return { ipad, active: ipad && !!coverPath, coverUrl: coverPath ? fileUrl(coverPath) : null };
  }, []);
  const [state, setState] = useState(readState);

  // Re-read whenever the iPad session flips, a storage event lands, or the
  // route changes. All three cases collapse into one setState so React
  // schedules a single re-render.
  useEffect(() => {
    const refresh = () => setState(readState());
    window.addEventListener("yacht-ipad-session-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("yacht-ipad-session-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [readState]);

  useEffect(() => { setState(readState()); }, [location.pathname, readState]);

  // Selection policy applies to the whole kiosk, even without a cover image.
  useEffect(() => {
    document.body.classList.toggle("ipad-app", state.ipad);
    return () => document.body.classList.remove("ipad-app");
  }, [state.ipad]);

  // Body class + image preload. `new Image()` starts the network fetch
  // synchronously so the browser has the bytes ready before React commits
  // the <img> that follows below — no black-scrim flash on first render.
  useEffect(() => {
    if (state.active) {
      document.body.classList.add("ipad-cover");
      if (state.coverUrl) {
        const preload = new Image();
        preload.src = state.coverUrl;
      }
    } else {
      document.body.classList.remove("ipad-cover");
    }
    return () => document.body.classList.remove("ipad-cover");
  }, [state.active, state.coverUrl]);

  if (!state.active) return null;

  return (
    <div
      aria-hidden="true"
      // `100dvw × 100dvh` explicitly (not `inset-0`) so the hero paints
      // across every dynamic-viewport pixel including the iOS safe-area
      // gutters. `inset-0` alone anchors to the padded content box in
      // some Safari builds, leaving a warm-sand / white band beside the
      // scrim whenever Split View resizes the webview.
      className="pointer-events-none fixed top-0 left-0 overflow-hidden -z-10"
      style={{ width: "100dvw", height: "100dvh" }}
      data-testid="ipad-cover-hero"
    >
      <img
        src={state.coverUrl}
        alt=""
        className="w-full h-full object-cover"
        // `eager` fetches with the highest browser priority; `sync` decode
        // avoids the async-decode flicker that Safari sometimes shows on
        // large above-the-fold hero images.
        loading="eager"
        decoding="sync"
        fetchpriority="high"
      />
      {/* Uniform ≈72 % black scrim keeps the image reading as texture, not
          content, and preserves AA contrast against light glass cards. */}
      <div className="absolute inset-0 bg-[rgba(8,12,20,0.72)]" />
    </div>
  );
}
